import { Injectable } from '@angular/core';
import { ErrorListService } from '../../../../reusable-components/error-list/error-list.service';
import { RoomSelectionService } from '../room-selection.service';
import { LeadTaskService } from '../../lead-task.service';
import { UsageSpaceCategory, UsageSpaceInfo } from '../../../../lead.model';

@Injectable()
export class CreateRoomService {
  private readonly fillAllFieldsError =
    'components.create-room.fill-all-fields-error';
  private readonly sizeZeroError = 'components.create-room.size-zero-error';
  private readonly numberRoomsError =
    'components.create-room.number-rooms-error';
  private readonly roomCategoryError =
    'components.create-room.room-category-error';

  public readonly errorListService: ErrorListService;
  private readonly createdRoomInfo: UsageSpaceInfo;

  constructor(
    private roomSelectionService: RoomSelectionService,
    private leadTaskService: LeadTaskService,
  ) {
    this.errorListService =
      roomSelectionService.getCreateRoomInput().errorListService;
    this.errorListService.addError('components.create-room.default-error');
    this.createdRoomInfo = this.getEmptyRoom();
    this.validateRoom();
  }

  startSelfCreatedRoomExamination() {
    const isHotelRoom = this.getCategory() === 'HOTEL_GAESTEZIMMER';
    // Enforce mutual exclusivity: hotel rooms have no size, other rooms have no room count
    if (isHotelRoom) {
      this.createdRoomInfo.size = 0;
    } else {
      this.createdRoomInfo.numberRooms = 0;
    }
    const newId = this.leadTaskService.addSelfCreatedRoomInfo(
      this.createdRoomInfo,
    );
    this.roomSelectionService.startRoomExamination(newId);
  }

  discardSelfCreatedRoom() {
    this.roomSelectionService.startRoomList();
  }

  getNgComponentOutletInputObservable() {
    return this.roomSelectionService.ngComponentOutletInput$;
  }

  validateRoom(): void {
    const isHotelRoom = this.getCategory() === 'HOTEL_GAESTEZIMMER';

    if (
      this.getName().length === 0 ||
      this.getCategory() === 'UNBEKANNT' ||
      (!isHotelRoom && this.getSize() <= 0) ||
      (isHotelRoom && this.getNumberRooms() <= 0)
    ) {
      this.errorListService.addError(this.fillAllFieldsError);
    } else {
      this.errorListService.removeError(this.fillAllFieldsError);
    }

    if (this.getCategory() === 'UNBEKANNT') {
      this.errorListService.addError(this.roomCategoryError);
    } else {
      this.errorListService.removeError(this.roomCategoryError);
    }

    if (!isHotelRoom && this.getSize() <= 0) {
      this.errorListService.addError(this.sizeZeroError);
    } else {
      this.errorListService.removeError(this.sizeZeroError);
    }

    if (isHotelRoom && this.getNumberRooms() <= 0) {
      this.errorListService.addError(this.numberRoomsError);
    } else {
      this.errorListService.removeError(this.numberRoomsError);
    }
  }

  getEmptyRoom(): UsageSpaceInfo {
    return {
      numberRooms: 0,
      name: '',
      size: 0,
      category: 'UNBEKANNT',
    };
  }

  getName(): string {
    return this.createdRoomInfo.name;
  }

  setName(name: string): void {
    this.createdRoomInfo.name = name;
    this.validateRoom();
  }

  getSize(): number {
    return this.createdRoomInfo.size;
  }

  setSize(size: string): void {
    if (this.leadTaskService.validateUsageSpaceSize(size)) {
      this.createdRoomInfo.size = Number.parseInt(size);
      this.validateRoom();
    }
  }

  getNumberRooms(): number {
    return this.createdRoomInfo.numberRooms;
  }

  setNumberRooms(numberRooms: number) {
    this.createdRoomInfo.numberRooms = numberRooms;
    this.validateRoom();
  }

  getTranslatableCategory(): string {
    return 'enums.category.' + this.createdRoomInfo.category;
  }

  getCategory(): UsageSpaceCategory {
    return this.createdRoomInfo.category;
  }

  setCategory(category: UsageSpaceCategory) {
    this.createdRoomInfo.category = category;
    this.validateRoom();
  }
}
